import { cx } from "@linaria/core";
import type { ReactNode } from "react";
import { useRef, useEffect } from "react";

import type { User } from "../../../../models/User";
import { CSSRefTransition } from "../../../components/atoms/transition.css";
import { LogoutIcon } from "../../../components/icons/LogoutIcon";
import { UserIcon } from "../../../components/icons/UserIcon";
import { Tooltip } from "../../../components/poppers/Tooltip/Tooltip";
import { useOverlay } from "../../../hooks/effect/useOverlay";
import { getLogoutUrl } from "../../../utils/api/loginConnector";

import * as styles from "./styles/UserButton";

type Props = {
  className: string;
  headerRef: React.RefObject<HTMLDivElement | null>;
  user: User;
};

export function UserButton({ className, headerRef, user }: Props): ReactNode {
  const ref = useRef<HTMLButtonElement>(null);
  const overlay = useOverlay();

  useEventListener("keydown", (evt) => {
    if (evt.key === "Escape") {
      overlay.close();
    }
  });

  return (
    <>
      <Tooltip
        containerRef={ref}
        mountIn={headerRef.current}
        disabled={overlay.isOpen}
        placement="bottom"
        offsetPx={-22}
        label="Min profil"
      >
        <button
          ref={ref}
          className={cx(
            className,
            styles.togglerButtonCls,
            overlay.isOpen && "active",
          )}
          onClick={overlay.toggle}
        >
          <span className={styles.togglerButtonContentCls}>
            <UserIcon />
          </span>
        </button>
      </Tooltip>

      <CSSRefTransition
        in={overlay.isOpen}
        timeout={styles.overlayTransitionDurationMs}
        mountOnEnter
        unmountOnExit
      >
        {(ref) => (
          <div ref={ref} className={styles.overlayCls} onClick={overlay.close}>
            <div
              className={styles.popperCls}
              onClick={(evt) => {
                evt.stopPropagation();
              }}
            >
              <div className={styles.popperContentCls}>
                {user.name}
                <br />
                <a
                  className={styles.logoutLinkCls}
                  href={getLogoutUrl()}
                  rel="noopener noreferrer"
                >
                  <LogoutIcon />
                  Log ud
                </a>
              </div>
            </div>
          </div>
        )}
      </CSSRefTransition>
    </>
  );
}

function useEventListener<K extends keyof WindowEventMap>(
  type: K,
  listener: ((this: Window, ev: WindowEventMap[K]) => void) | undefined,
  options?: boolean | AddEventListenerOptions,
): void {
  useEffect(() => {
    if (!listener) {
      return;
    }

    window.addEventListener(type, listener, options);

    return () => {
      window.removeEventListener(type, listener, options);
    };
  }, [type, listener, options]);
}
